import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdEdit, MdSubject, MdCalendarToday } from 'react-icons/md'
import { FocusCustomer } from 'api/company/useFocusCustomers'
import { updateCompanySummary } from 'api/company/updateCompanySummary'
import swal from 'sweetalert2'

type Props = {
  customer: FocusCustomer
  onRefresh: () => void
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

export const FocusCustomerCard = ({ customer, onRefresh }: Props) => {
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)
  const [summaryInput, setSummaryInput] = useState(customer.summary ?? '')
  const [saving, setSaving] = useState(false)

  const handleSaveSummary = async () => {
    setSaving(true)
    try {
      await updateCompanySummary(customer.companyId, summaryInput || null)
      setEditOpen(false)
      onRefresh()
    } catch {
      swal.fire({
        icon: 'error',
        text: '要約の更新に失敗しました。',
        confirmButtonText: 'OK'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* カードヘッダー */}
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/company/${customer.companyId}`)}
            className="text-base font-bold text-gray-800 hover:text-[#01695C] hover:underline"
          >
            {customer.companyName}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setSummaryInput(customer.summary ?? '')
            setEditOpen(true)
          }}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-[#01695C]"
          title="編集"
        >
          <MdEdit size={18} />
        </button>
      </div>
      {/* 要約 */}
      <div className="mx-5 mb-5 mt-4">
        <h4 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <MdSubject size={18} />
          要約
        </h4>
        <div className="mt-4 rounded-lg bg-emerald-50 p-3">
          <p className="text-sm leading-relaxed text-gray-700">
            {customer.summary ?? '（要約未設定）'}
          </p>
        </div>
      </div>
      {/* タスク一覧 */}
      <div className="mx-5 mb-5 mt-4 border-t border-gray-100 pt-4">
        <h4 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <MdCalendarToday size={18} />
          タスク
        </h4>
        {customer.tasks.length === 0 ? (
          <p className="text-xs text-gray-400">タスクはありません。</p>
        ) : (
          <div className="space-y-3">
            {customer.tasks.map((task) => (
              <div key={task.id} className="flex gap-3">
                <div className="flex-1 rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-800">
                      {task.title}
                    </span>
                    {task.dueDate && (
                      <span className="flex-shrink-0 text-xs text-gray-500">
                        {formatDate(task.dueDate)}
                      </span>
                    )}
                  </div>
                  {task.description && (
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      {task.description}
                    </p>
                  )}
                  {task.creator && (
                    <p className="mt-1.5 text-xs text-gray-400">
                      担当: {task.creator}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 要約編集モーダル */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold text-gray-800">要約を編集</h2>
            <p className="mb-2 text-sm text-gray-600"><span className="mr-1 text-xs text-gray-400">会社名</span>{customer.companyName}</p>
            <textarea
              value={summaryInput}
              onChange={(e) => setSummaryInput(e.target.value)}
              rows={5}
              placeholder="要約を入力してください"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleSaveSummary}
                disabled={saving}
                className="rounded-lg bg-[#01695C] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-60"
              >
                {saving ? '保存中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
